<script>
import { createPlayground } from 'livecodes'

// ── CDN AMD loader ────────────────────────────────────────────────────────────
const MONACO_VERSION = '0.55.1'
const MONACO_BASE    = 'https://cdn.jsdelivr.net/npm/monaco-editor@' + MONACO_VERSION + '/min/vs'

var _monacoPromise = null

function getMonaco() {
  if (_monacoPromise) return _monacoPromise

  if (typeof window === 'undefined') {
    return Promise.reject(new Error('monaco requires a browser environment'))
  }

  if (window.monaco) {
    _monacoPromise = Promise.resolve(window.monaco)
    return _monacoPromise
  }

  _monacoPromise = new Promise(function (resolve, reject) {
    function loadMain() {
      window.require.config({ paths: { vs: MONACO_BASE } })
      window.require(['vs/editor/editor.main'], function () {
        resolve(window.monaco)
      })
    }

    // AMD loader may already be present (e.g. consumer loaded Monaco themselves)
    if (window.require && window.require.config) {
      loadMain()
      return
    }

    var script = document.createElement('script')
    script.src = MONACO_BASE + '/loader.js'
    script.onload  = loadMain
    script.onerror = function () { reject(new Error('Failed to load Monaco loader from CDN')) }
    document.head.appendChild(script)
  })

  return _monacoPromise
}

// ── Constants ─────────────────────────────────────────────────────────────────
var TABS = [
  { key: 'template', label: 'Template' },
  { key: 'script',   label: 'Script'   },
  { key: 'style',    label: 'Style'    },
]

var DEFAULTS = {
  vue3: {
    template: '<div>\n  <h1>{{ message }}</h1>\n</div>',
    script:   "import { ref } from 'vue'\n\nconst message = ref('Hello Vue 3!')",
    style:    '',
  },
  vue2: {
    template: '<div>\n  <h1>{{ message }}</h1>\n</div>',
    script:   "/** @type {import('vue').ComponentOptions} */\nexport default {\n  data() {\n    return {\n      message: 'Hello Vue 2!',\n    }\n  },\n}",
    style:    '',
  },
}

var EDITOR_OPTS = {
  minimap:              { enabled: false },
  automaticLayout:      true,
  fontSize:             14,
  scrollBeyondLastLine: false,
  tabSize:              2,
}

// ── Type setup (once per Monaco instance) ────────────────────────────────────
var monacoConfigured = false
function configureMonacoTypes(monaco) {
  if (monacoConfigured) return
  monacoConfigured = true

  monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
    target:                       monaco.languages.typescript.ScriptTarget.ESNext,
    module:                       monaco.languages.typescript.ModuleKind.ESNext,
    moduleResolution:             monaco.languages.typescript.ModuleResolutionKind.NodeJs,
    allowSyntheticDefaultImports: true,
    strict:                       false,
    noEmit:                       true,
  })

  monaco.languages.typescript.typescriptDefaults.addExtraLib([
    "declare module 'vue' {",
    '  export interface Ref<T = any> { value: T }',
    '  export function ref<T>(value: T): Ref<T>',
    '  export function ref<T = any>(): Ref<T | undefined>',
    '  export function computed<T>(getter: () => T): { readonly value: T }',
    '  export function reactive<T extends object>(target: T): T',
    '  export function watch(source: any, cb: any, opts?: any): () => void',
    '  export function watchEffect(effect: () => void): () => void',
    '  export function onMounted(fn: () => void): void',
    '  export function onBeforeUnmount(fn: () => void): void',
    '  export function nextTick(fn?: () => void): Promise<void>',
    '  export function defineProps<T>(): T',
    '  export function defineEmits<T>(): T',
    '  export function defineComponent<T extends object>(options: T): T',
    '}',
  ].join('\n'), 'file:///node_modules/vue/index.d.ts')

  monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
    target:                       monaco.languages.typescript.ScriptTarget.ES2015,
    allowSyntheticDefaultImports: true,
    checkJs:                      true,
    allowJs:                      true,
  })

  monaco.languages.typescript.javascriptDefaults.addExtraLib([
    'interface VueOptions {',
    '  el?: string | Element',
    '  name?: string',
    '  data?(this: any): Record<string, any>',
    '  props?: string[] | Record<string, any>',
    '  computed?: Record<string, ((this: any) => any) | { get(this: any): any; set(this: any, v: any): void }>',
    '  methods?: Record<string, (this: any, ...args: any[]) => any>',
    '  watch?: Record<string, any>',
    '  components?: Record<string, any>',
    '  template?: string',
    '  render?(this: any, createElement: any): any',
    '  created?(this: any): void',
    '  mounted?(this: any): void',
    '  beforeDestroy?(this: any): void',
    '  destroyed?(this: any): void',
    '}',
    'declare class Vue {',
    '  constructor(options?: VueOptions)',
    '  $data: Record<string, any>',
    '  $el: Element',
    '  $emit(event: string, ...args: any[]): this',
    '  $nextTick(fn?: () => void): Promise<void>',
    '  $set<T>(obj: Record<string, T> | T[], key: string | number, value: T): T',
    '  static component(name: string, options?: VueOptions): any',
    '  static use(plugin: any): void',
    '  static nextTick(fn?: () => void): Promise<void>',
    '}',
    "declare module 'vue' {",
    '  export type ComponentOptions = VueOptions',
    '  export default Vue',
    '}',
  ].join('\n'), 'file:///node_modules/vue/index.d.ts')
}

// ── Helpers ───────────────────────────────────────────────────────────────────
var VUE2_TYPE_HEADER = "/** @type {import('vue').ComponentOptions} */\n"

function withVue2Header(content) {
  return content.trimLeft().indexOf('/** @type') === 0
    ? content
    : VUE2_TYPE_HEADER + content
}

function stripExportDefault(script) {
  return script.replace(/export\s+default\s*/, '')
}

function ensureExportDefault(script) {
  if (/export\s+default/.test(script)) return script
  var trimmed = script.trim()
  if (trimmed.indexOf('/**') === 0) {
    var end = trimmed.indexOf('*/') + 2
    return trimmed.slice(0, end) + '\nexport default ' + trimmed.slice(end).trimLeft()
  }
  return 'export default ' + trimmed
}

function parseSfc(sfc) {
  var tpl    = sfc.match(/<template(?:\s[^>]*)?>([\s\S]*)<\/template>/)
  var script = sfc.match(/<script(?:\s[^>]*)?>([\s\S]*?)<\/script>/)
  var style  = sfc.match(/<style(?:\s[^>]*)?>([\s\S]*?)<\/style>/)
  return {
    template: (tpl    && tpl[1]    ? tpl[1].trim()    : ''),
    script:   (script && script[1] ? script[1].trim() : ''),
    style:    (style  && style[1]  ? style[1].trim()  : ''),
  }
}

function loadStorage() {
  try {
    var raw = localStorage.getItem('platon-sfc')
    if (raw) return JSON.parse(raw)
  } catch (_) {}
  return {
    vue3: { template: DEFAULTS.vue3.template, script: DEFAULTS.vue3.script, style: DEFAULTS.vue3.style },
    vue2: { template: DEFAULTS.vue2.template, script: DEFAULTS.vue2.script, style: DEFAULTS.vue2.style },
  }
}

// ── Component ─────────────────────────────────────────────────────────────────
export default {
  name: 'VueSfcEditor',

  props: {
    lang:              { type: String,  default: 'vue2' },
    showPreview:       { type: Boolean, default: false  },
    live:              { type: Boolean, default: false  },
    value:             { type: String,  default: null   },
    withExportDefault: { type: Boolean, default: true   },
  },

  data: function () {
    return {
      activeTab: 'template',
      tabs:      TABS,
    }
  },

  created: function () {
    this._monaco          = null
    this._eds             = { template: null, script: null, style: null }
    this._storage         = loadStorage()
    this._pg              = null
    this._debounceTimer   = null
    this._suppressWatch   = false
    this._settingFromProp = false
    this._destroyed       = false
  },

  watch: {
    lang: function (newLang) { this._switchLang(newLang) },
    showPreview: function (val) {
      if (val) this._runPreview()
      else this._destroyPreview()
    },
    live: function (val) {
      if (val && this.showPreview) this._runPreview()
    },
    value: function (newVal) {
      if (newVal == null || newVal === this._sfcContent()) return
      this._setFromProp(newVal)
    },
  },

  mounted: function () {
    var self = this
    getMonaco().then(function (monaco) {
      if (self._destroyed) return
      self._monaco = monaco
      configureMonacoTypes(monaco)
      self._mountEditors()
      if (self.value != null) self._setFromProp(self.value)
      self.$emit('input', self._sfcContent())
      if (self.showPreview) self._runPreview()
    })
  },

  beforeDestroy: function () {
    this._destroyed = true
    clearTimeout(this._debounceTimer)
    this._destroyPreview()
    var eds = this._eds
    Object.keys(eds).forEach(function (k) {
      var ed = eds[k]
      if (!ed) return
      var model = ed.getModel()
      if (model) model.dispose()
      ed.dispose()
    })
  },

  methods: {
    _scriptUri: function (lang) {
      return this._monaco.Uri.parse(lang === 'vue2' ? 'file:///script.js' : 'file:///script.ts')
    },

    _freshModel: function (content, language, uriStr) {
      var monaco = this._monaco
      var uri    = monaco.Uri.parse(uriStr)
      var exist  = monaco.editor.getModel(uri)
      if (exist) exist.dispose()
      return monaco.editor.createModel(content, language, uri)
    },

    _mountEditors: function () {
      var monaco = this._monaco
      var stored = this._storage[this.lang]
      var self   = this

      this._eds.template = monaco.editor.create(this.$refs.templateEl, Object.assign({}, EDITOR_OPTS, {
        model: this._freshModel(stored.template, 'html', 'file:///template.html'),
      }))
      this._eds.template.onDidChangeModelContent(function () {
        if (self._suppressWatch || self._settingFromProp) return
        self._storage[self.lang].template = self._eds.template.getValue()
        self._save()
        self._scheduleLive()
        self.$emit('input', self._sfcContent())
      })

      var scriptContent = this.lang === 'vue2' ? withVue2Header(stored.script) : stored.script
      var scriptLang    = this.lang === 'vue2' ? 'javascript' : 'typescript'
      var scriptUri     = this.lang === 'vue2' ? 'file:///script.js' : 'file:///script.ts'
      this._eds.script = monaco.editor.create(this.$refs.scriptEl, Object.assign({}, EDITOR_OPTS, {
        model: this._freshModel(scriptContent, scriptLang, scriptUri),
      }))
      this._attachScriptListener()

      this._eds.style = monaco.editor.create(this.$refs.styleEl, Object.assign({}, EDITOR_OPTS, {
        model: this._freshModel(stored.style, 'css', 'file:///style.css'),
      }))
      this._eds.style.onDidChangeModelContent(function () {
        if (self._suppressWatch || self._settingFromProp) return
        self._storage[self.lang].style = self._eds.style.getValue()
        self._save()
        self._scheduleLive()
        self.$emit('input', self._sfcContent())
      })
    },

    _attachScriptListener: function () {
      var self = this
      this._eds.script.onDidChangeModelContent(function () {
        if (self._suppressWatch || self._settingFromProp) return
        self._storage[self.lang].script = self._eds.script.getValue()
        self._save()
        self._scheduleLive()
        self.$emit('input', self._sfcContent())
      })
    },

    _switchLang: function (lang) {
      if (!this._monaco || !this._eds.template) return
      var monaco  = this._monaco
      var stored  = this._storage[lang]
      this._suppressWatch = true

      this._eds.template.setValue(stored.template)
      this._eds.style.setValue(stored.style)

      var oldModel         = this._eds.script.getModel()
      var newScriptContent = lang === 'vue2' ? withVue2Header(stored.script) : stored.script
      var newScriptUri     = lang === 'vue2' ? 'file:///script.js' : 'file:///script.ts'
      var newModel         = this._freshModel(newScriptContent, lang === 'vue2' ? 'javascript' : 'typescript', newScriptUri)
      this._eds.script.setModel(newModel)
      oldModel.dispose()
      this._attachScriptListener()

      this._suppressWatch = false
      this.activeTab = 'template'

      if (this.showPreview) this._runPreview()
      else this._destroyPreview()
    },

    _setFromProp: function (sfcString) {
      var parsed = parseSfc(sfcString)
      var script = parsed.script
      if (!this.withExportDefault) script = ensureExportDefault(script)
      var scriptVal = this.lang === 'vue2' ? withVue2Header(script) : script
      this._settingFromProp = true
      this._storage[this.lang] = { template: parsed.template, script: scriptVal, style: parsed.style }
      if (this._eds.template) this._eds.template.setValue(parsed.template)
      if (this._eds.script)   this._eds.script.setValue(scriptVal)
      if (this._eds.style)    this._eds.style.setValue(parsed.style)
      this._settingFromProp = false
      this._save()
    },

    _save: function () {
      try { localStorage.setItem('platon-sfc', JSON.stringify(this._storage)) } catch (_) {}
    },

    _sfcContent: function () {
      var parts  = this._storage[this.lang]
      var script = parts.script
      if (!this.withExportDefault) script = stripExportDefault(script)
      var scriptTag = this.lang === 'vue3'
        ? '<script setup>\n' + script + '\n<\/script>'
        : '<script>\n' + script + '\n<\/script>'
      var result = ['<template>\n' + parts.template + '\n</template>', scriptTag]
      if (parts.style && parts.style.trim()) result.push('<style scoped>\n' + parts.style + '\n</style>')
      return result.join('\n\n')
    },

    _buildPreviewConfig: function () {
      if (this.lang === 'vue2') {
        var parts      = this._storage['vue2']
        var optionsStr = parts.script.replace(/export\s+default\s+/, '').trim()
        return {
          scripts: ['https://cdn.jsdelivr.net/npm/vue@2.6.14/dist/vue.min.js'],
          markup:  { language: 'html',       content: '<div id="__va__">\n' + parts.template + '\n</div>', hideTitle: true },
          script:  { language: 'javascript', content: 'new Vue(Object.assign({ el: \'#__va__\' }, ' + optionsStr + '))', hideTitle: true },
          style:   { language: 'css',        content: parts.style || '', hideTitle: true },
          mode:    'result',
          tools:   { status: 'closed' },
        }
      }
      return {
        activeEditor: 'markup',
        markup: { language: 'vue', content: this._sfcContent() },
        style:  { language: 'css', content: '', hideTitle: true },
        script: { language: 'javascript', content: '', hideTitle: true },
        mode:   'result',
        tools:  { status: 'closed' },
      }
    },

    _runPreview: function () {
      var self = this
      var el   = this.$refs.previewEl
      if (!el) return
      this._destroyPreview()
      createPlayground(el, { config: this._buildPreviewConfig() }).then(function (pg) {
        self._pg = pg
      })
    },

    _destroyPreview: function () {
      if (this._pg) {
        this._pg.destroy()
        this._pg = null
      }
    },

    _scheduleLive: function () {
      if (!this.live || !this.showPreview) return
      var self = this
      clearTimeout(this._debounceTimer)
      this._debounceTimer = setTimeout(function () { self._runPreview() }, 800)
    },

    runPreview: function () {
      this._runPreview()
    },
  },

  render: function (h) {
    var self = this

    var editorSide = h('div', {
      style: {
        width:         this.showPreview ? '50%' : '100%',
        display:       'flex',
        flexDirection: 'column',
        height:        '100%',
      },
    }, [
      h('div', { class: 'sfc-tabbar' }, [
        this.tabs.map(function (tab) {
          return h('button', {
            key: tab.key,
            class: ['sfc-tab', self.activeTab === tab.key ? 'sfc-tab--active' : ''],
            attrs: { type: 'button' },
            on: { click: function () { self.activeTab = tab.key } },
          }, [tab.label])
        }),
        this.showPreview
          ? h('button', {
              class: ['sfc-run-btn', this.live ? 'sfc-run-btn--disabled' : ''],
              attrs: { type: 'button', disabled: this.live },
              on: { click: this.runPreview },
            }, ['▶ Run'])
          : null,
      ]),
      h('div', { style: 'flex:1;min-height:0;position:relative;' }, [
        h('div', { ref: 'templateEl', directives: [{ name: 'show', value: this.activeTab === 'template' }], style: 'position:absolute;top:0;right:0;bottom:0;left:0;' }),
        h('div', { ref: 'scriptEl',   directives: [{ name: 'show', value: this.activeTab === 'script'   }], style: 'position:absolute;top:0;right:0;bottom:0;left:0;' }),
        h('div', { ref: 'styleEl',    directives: [{ name: 'show', value: this.activeTab === 'style'    }], style: 'position:absolute;top:0;right:0;bottom:0;left:0;' }),
      ]),
    ])

    var previewSide = this.showPreview
      ? h('div', { ref: 'previewEl', style: 'width:50%;height:100%;border-left:1px solid #3c3c3c;' })
      : null

    return h('div', { style: 'display:flex;height:100%;overflow:hidden;' }, [editorSide, previewSide])
  },
}
</script>

<style>
.sfc-tabbar {
  display: flex;
  align-items: center;
  background: #252526;
  border-bottom: 1px solid #3c3c3c;
  padding: 0 4px;
  flex-shrink: 0;
}
.sfc-tab {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: #9d9d9d;
  cursor: pointer;
  transition: color 0.15s;
  margin-bottom: -1px;
}
.sfc-tab:hover { color: #cccccc; }
.sfc-tab--active { color: #4fc1ff; border-bottom-color: #4fc1ff; background: #1e1e1e; }
.sfc-run-btn {
  margin-left: auto;
  margin-right: 8px;
  padding: 4px 12px;
  font-size: 12px;
  background: #0e639c;
  color: #fff;
  border: none;
  border-radius: 3px;
  cursor: pointer;
}
.sfc-run-btn:hover { background: #1177bb; }
.sfc-run-btn--disabled { background: #3c3c3c; color: #6b6b6b; cursor: not-allowed; }
</style>
